import FormSubmitProps from "@/src/types/FormSubmitProps";
import Typography from "@mui/material/Typography";
import { AbstractCase } from "@/src/types/Entities/AbstractCase";
import summaryStyle from "@/src/components/Form/ContractLaw/Summary/summaryStyle";
import React, { useEffect } from "react";
import { useLocation } from "react-router";
import { useForm } from "@/src/provider/Form/FormProvider";
import { getCaseLink } from "@/src/services/productService";
import { Box } from "@mui/material";
import Grid from "@mui/material/Grid";
import ContractData from "@/src/components/Form/ContractLaw/Summary/ContractData";
import ContentBox from "@/src/components/Box/ContentBox/ContentBox";
import SummaryDetailsItem from "@/src/components/Form/Common/SummaryDetailsItem/SummaryDetailsItem";
import FormSubmit from "@/src/components/Form/Common/FormSubmit/FormSubmit";
import InsuranceData from "@/src/components/Form/Settlement/Summary/InsuranceData";
import ContractDescription from "@/src/components/Form/ContractLaw/Summary/ContractDescription";
import SummaryDetailsPersonItems from "@/src/components/Form/Common/SummaryDetailsItem/SummaryDetailsPersonItems";
import { arrayUtils } from "@/src/services/utils";
import { isFlexServiceProduct } from "@/src/services/advocardService";
import EmailVerificationModal from "@/src/components/FormInfoModal/EmailVerificationModal";

type EmploymentContractSummaryProps = {
  product: AbstractCase;
  submitProps: FormSubmitProps;
};

export default function EmploymentContractSummary({ product, submitProps }: EmploymentContractSummaryProps) {
  const location = useLocation();
  const stateField = location.state?.field;
  const { handleSubmit } = useForm();

  useEffect(() => {
    const el = document.getElementById(stateField);
    el && el.scrollIntoView();
  }, [stateField]);

  return (
    <ContentBox>
      <Typography variant={"h5"}>
        Ihre Angaben
      </Typography>
      <Grid container>
        <Box id={"contractData"} />
        <ContractData caseData={product} />

        <SummaryDetailsItem caseData={product} fieldPath={"employmentStartDate"} fieldType={"date"} />

        {product.$employmentEndDate && <SummaryDetailsItem caseData={product} fieldPath={"employmentEndDate"} />}

        <SummaryDetailsItem fieldPath={"grossSalary"} caseData={product} fieldType={"money"} />

        <SummaryDetailsItem fieldPath={"employmentRegularWorkplace"} caseData={product} />

        {product.employmentRegularWorkplace === "other" && (
          <>
            <SummaryDetailsItem fieldPath={"employmentRegularWorkplaceOtherAddressDescription"} caseData={product} />
            <SummaryDetailsItem fieldPath={"employmentRegularWorkplaceOtherAddress.streetAddress"} caseData={product} />
            <SummaryDetailsItem fieldPath={"employmentRegularWorkplaceOtherAddress.postalCode"} caseData={product} />
            <SummaryDetailsItem fieldPath={"employmentRegularWorkplaceOtherAddress.addressLocality"} caseData={product} />
          </>
        )}

        {product.caseCategory !== "advisory" && <SummaryDetailsItem fieldPath={"reasonsForDispute"} caseData={product} fieldType={"multipleOptions"} />}

        {arrayUtils.getArraysIntersect(product.reasonsForDispute, ["salary"]) && (
          <>
            <SummaryDetailsItem fieldPath={"salaryOutstandingStartDate"} caseData={product} fieldType={"date"} />
            <SummaryDetailsItem fieldPath={"salaryOutstandingEndDate"} caseData={product} fieldType={"date"} />
          </>
        )}

        {arrayUtils.getArraysIntersect(product.reasonsForDispute, ["vacation"]) && (
          <>
            <SummaryDetailsItem fieldPath={"vacationDaysLeft"} caseData={product} fieldType={"float"} />
            <SummaryDetailsItem fieldPath={"vacationDenialReason"} caseData={product} />
            <SummaryDetailsItem fieldPath={"yearOfVacationClaim"} caseData={product} />
          </>
        )}

        {arrayUtils.getArraysIntersect(product.reasonsForDispute, ["vacation"]) && product.subjectOfDispute === "money" && (
          <>
            <SummaryDetailsItem fieldPath={"grossSalarySumLastThreeMonths"} caseData={product} fieldType={"money"} />
            <SummaryDetailsItem fieldPath={"weeklyWorkingHours"} caseData={product} />
          </>
        )}

        {arrayUtils.getArraysIntersect(product.reasonsForDispute, ["overtime"]) && (
          <>
            <SummaryDetailsItem fieldPath={"overtimeAccumulatedStartDate"} caseData={product} fieldType={"date"} />
            <SummaryDetailsItem fieldPath={"overtimeAccumulatedEndDate"} caseData={product} fieldType={"date"} />
            <SummaryDetailsItem fieldPath={"hoursLeisureCompensation"} caseData={product} fieldType={"float"} />
            <SummaryDetailsItem fieldPath={"hasOvertimeRecord"} caseData={product} fieldType={"yesNo"} />
          </>
        )}

        {arrayUtils.getArraysIntersect(product.reasonsForDispute, ["overtime"]) && product.subjectOfDispute === "money" && (
          <>
            <SummaryDetailsItem fieldPath={"overtimeAccumulatedTotal"} caseData={product} fieldType={"float"} />
            <SummaryDetailsItem fieldPath={"weeklyWorkingHours"} caseData={product} />
            <SummaryDetailsItem fieldPath={"hasOvertimeSurcharge"} caseData={product} fieldType={"yesNo"} />
            <SummaryDetailsItem fieldPath={"overtimeSurchargeDetails"} caseData={product} />
            <SummaryDetailsItem fieldPath={"hasOvertimeRecord"} caseData={product} fieldType={"yesNo"} />
          </>
        )}

        {arrayUtils.getArraysIntersect(product.reasonsForDispute, ["workCertificate"]) && (
          <>
            <SummaryDetailsItem fieldPath={"workCertificateRequest"} caseData={product} />
            <SummaryDetailsItem fieldPath={"workCertificateIssuanceDenied"} caseData={product} fieldType={"yesNo"} />
            <SummaryDetailsItem fieldPath={"workCertificateIssuanceDenialReason"} caseData={product} />
          </>
        )}

        {product.workCertificateRequest === "requestCorrection" && (
          <>
            <SummaryDetailsItem fieldPath={"workCertificateReceivedAtDate"} caseData={product} fieldType={"date"} />
            <SummaryDetailsItem fieldPath={"workCertificateCorrectionRequest"} caseData={product} fieldType={"multipleOptions"} />
          </>
        )}

        {arrayUtils.getArraysIntersect(product.reasonsForDispute, ["parentalLeave"]) && (
          <>
            <SummaryDetailsItem fieldPath={"parentalLeaveStartDate"} caseData={product} fieldType={"date"} />
            <SummaryDetailsItem fieldPath={"parentalLeaveEndDate"} caseData={product} fieldType={"date"} />
            <SummaryDetailsItem fieldPath={"hasTakenParentalLeaveBefore"} caseData={product} fieldType={"yesNo"} />
            <SummaryDetailsItem fieldPath={"parentalLeaveSplit"} caseData={product} fieldType={"yesNo"} />
            <SummaryDetailsItem fieldPath={"parentalLeaveRequestOnTime"} caseData={product} fieldType={"yesNo"} />
            <SummaryDetailsItem fieldPath={"parentalLeaveRequestDenied"} caseData={product} fieldType={"yesNo"} />
            <SummaryDetailsItem fieldPath={"parentalLeaveRequestDeniedReason"} caseData={product} />
          </>
        )}

        {arrayUtils.getArraysIntersect(product.reasonsForDispute, ["partTime"]) && (
          <>
            <SummaryDetailsItem fieldPath={"partTimeStartDate"} caseData={product} fieldType={"date"} />
            <SummaryDetailsItem fieldPath={"partTimeDesiredWeeklyWorkingHours"} caseData={product} fieldType={"float"} />
            <SummaryDetailsItem fieldPath={"numberOfEmployees"} caseData={product} />
            <SummaryDetailsItem fieldPath={"partTimeDenialReason"} caseData={product} />
          </>
        )}

        {arrayUtils.getArraysIntersect(product.reasonsForDispute, ["bullying"]) && (
          <>
            <SummaryDetailsItem fieldPath={"incidentType"} caseData={product} />
            <SummaryDetailsItem fieldPath={"witnessCount"} caseData={product} />
            <SummaryDetailsItem fieldPath={"incidentStartDate"} caseData={product} fieldType={"date"} />
            <SummaryDetailsItem fieldPath={"incidentObjectiveClient"} caseData={product} />
          </>
        )}

        {arrayUtils.getArraysIntersect(product.reasonsForDispute, ["claimedPayment"]) && (
          <>
            <SummaryDetailsItem fieldPath={"claimedPaymentTypes"} caseData={product} fieldType={"multipleOptions"} />
            <SummaryDetailsItem fieldPath={"claimedPaymentReason"} caseData={product} />
          </>
        )}

        {arrayUtils.getArraysIntersect(product.reasonsForDispute, ["allegedDamage"]) && (
          <>
            <SummaryDetailsItem fieldPath={"allegedDamageTypes"} caseData={product} fieldType={"multipleOptions"} />
            <SummaryDetailsItem fieldPath={"allegedDamageDate"} caseData={product} fieldType={"date"} />
          </>
        )}

        {arrayUtils.getArraysIntersect(product.reasonsForDispute, ["writtenWarning"]) && (
          <>
            <SummaryDetailsItem fieldPath={"writtenWarningReceivedDate"} caseData={product} fieldType={"date"} />
            <SummaryDetailsItem fieldPath={"writtenWarningReason"} caseData={product} />
            <SummaryDetailsItem fieldPath={"writtenWarningIllegalReasons"} caseData={product} fieldType={"multipleOptions"} />
          </>
        )}

        {arrayUtils.getArraysIntersect(product.reasonsForDispute, ["termination"]) && (
          <>
            <SummaryDetailsItem fieldPath={"terminationAgreementOfferDate"} caseData={product} fieldType={"date"} />
            <SummaryDetailsItem fieldPath={"terminationAgreementDeadlineDate"} caseData={product} fieldType={"date"} />
            <SummaryDetailsItem fieldPath={"terminationAgreementIncludesSettlement"} caseData={product} fieldType={"yesNo"} />
            {product.terminationAgreementIncludesSettlement === "yes" && (
              <SummaryDetailsItem fieldPath={"offeredSettlementPayment"} caseData={product} fieldType={"money"} />
            )}
            <SummaryDetailsItem fieldPath={"terminationAgreementObjectivesClient"} caseData={product} fieldType={"multipleOptions"} />
          </>
        )}

        {arrayUtils.getArraysIntersect(product.reasonsForDispute, ["itemsToReturn"]) && (
          <>
            <SummaryDetailsItem caseData={product} fieldPath={"itemsToReturn"} fieldType={"multipleOptions"} />
            <SummaryDetailsItem caseData={product} fieldPath={"itemsToReturnReason"} />
            <SummaryDetailsItem caseData={product} fieldPath={"itemsToReturnRejectionReasons"} fieldType={"multipleOptions"} />
          </>
        )}

        {product.caseCategory !== "advisory" && (
          <>
            <Grid sx={summaryStyle.subHeadline} size={12}>
              <Typography variant={"h6"}>Angaben zum Arbeitgeber</Typography>
            </Grid>
            {product.opposingPartyType === "opposingPartyPerson" && <SummaryDetailsPersonItems basePath={"opposingPartyPerson"} caseData={product} />}
            {product.opposingPartyType === "opposingPartyLegalEntity" && (
              <>
                <SummaryDetailsItem fieldPath={"opposingPartyLegalEntity.organization.legalName"} caseData={product} />
                <SummaryDetailsItem fieldPath={"opposingPartyLegalEntity.organization.address.streetAddress"} caseData={product} />
                <SummaryDetailsItem fieldPath={"opposingPartyLegalEntity.organization.address.postalCode"} caseData={product} />
                <SummaryDetailsItem fieldPath={"opposingPartyLegalEntity.organization.address.addressLocality"} caseData={product} />
              </>
            )}
          </>
        )}

        <SummaryDetailsItem caseData={product} fieldPath={"contractualPartnerHasLawyer"} fieldType={"yesNo"} />
        {product.contractualPartnerHasLawyer && (
          <>
            <SummaryDetailsItem fieldPath={"opponentLawyer.gender"} caseData={product} />
            <SummaryDetailsItem fieldPath={"opponentLawyer.givenName"} caseData={product} />
            <SummaryDetailsItem fieldPath={"opponentLawyer.familyName"} caseData={product} />
            <SummaryDetailsItem fieldPath={"opponentLawFirm.organization.name"} caseData={product} />
            <SummaryDetailsItem fieldPath={"opponentLawFirm.organization.address.streetAddress"} caseData={product} />
            <SummaryDetailsItem fieldPath={"opponentLawFirm.organization.mainEmail.email"} caseData={product} />
            <SummaryDetailsItem fieldPath={"opponentLawFirm.organization.telephone"} caseData={product} />
          </>
        )}

        <Box id={"contractDescription"} />
        <ContractDescription title={"Beschreibung der Situation"} formData={product} />

        {!isFlexServiceProduct(product) && (
          <>
            <Box id={"insuranceData"} />
            <InsuranceData title={"Versicherung"} product={product} />
          </>
        )}
      </Grid>
      <form onSubmit={handleSubmit}>
        <FormSubmit
          sx={{ marginTop: "1rem" }}
          {...submitProps}
          hideBackButton={false}
          backLink={getCaseLink(product) + "/antrag/vertrag"}
          backText={"Alles bearbeiten"}
          nextText={"Angaben speichern"}
        />
      </form>
      <EmailVerificationModal />
    </ContentBox>
  );
}
